// CommunityDetails.jsx
import React, { useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Form,
  DropdownButton,
  Dropdown,
} from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Banner from "../../components/Banner";
import BreadcrumbBar from "../../components/Breadcrumb";
import ex1 from "../../assets/images/user.png";
import { AiOutlineDelete, AiOutlineLike } from "react-icons/ai";
import { HiOutlineDocumentText } from "react-icons/hi";
import { BsReply, BsThreeDotsVertical } from "react-icons/bs";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import DOMPurify from "dompurify";
import { FiEdit2 } from "react-icons/fi";
import { HiOutlineDocumentCheck } from "react-icons/hi2";
import { IoMdShare } from "react-icons/io";
import { IoArrowBack } from "react-icons/io5";

export default function CommunityDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();

  // Prefer state from list; fallback to global published by CommunityListing
  const item = useMemo(() => {
    if (state?.item) return state.item;
    const list = window.__COMMUNITY_RESULTS__ || [];
    return list.find((r) => String(r.id) === String(id)) || null;
  }, [id, state]);

  const [orderBy, setOrderBy] = useState("Most Recent");
  const [newComment, setNewComment] = useState("");

  const sortedComments = useMemo(() => {
    if (!item?.comments) return [];
    const clone = [...item.comments];
    if (orderBy === "Most Liked")
      clone.sort((a, b) => (b.likes || 0) - (a.likes || 0));
    // "Most Recent" keeps original order assuming list is already newest-first
    return clone;
  }, [item, orderBy]);

  if (!item) {
    return (
      <>
        <Banner heading="Community Hub" description="" />
        <Container className="py-5">
          <Card body className="text-center">
            We couldn’t find this discussion.{" "}
            <Button variant="link" onClick={() => navigate(-1)}>
              Go back
            </Button>
          </Card>
        </Container>
      </>
    );
  }

  const addComment = (e) => {
    e.preventDefault();
    const clean = DOMPurify.sanitize(newComment || "");
    if (!clean || !clean.replace(/<[^>]*>/g, "").trim()) return; // empty after stripping tags

    item.comments = [
      ...(item.comments || []),
      {
        id: Date.now(),
        user: "John Robinson",
        time: "just now",
        content: clean, // store sanitized HTML
        likes: 0,
        replies: [],
      },
    ];
    setNewComment("");
  };

  return (
    <>
      <Banner
        heading="Community Hub"
        description="Join conversations, share knowledge and learn from others."
      />

      <BreadcrumbBar
        items={[
          { label: "Home", href: "/" },
          { label: "Community", href: "/community" },
          { label: item.title },
        ]}
      />

      <Container className="list-section">
        <div className="profile-outer-box mb-0 mt-0">
          <Row className="align-items-center mb-3 g-2 community-heading">
            <Col md={6}>
              <h2 className="mb-md-0">
                <div className="header-section">
                  <button className="back-btn" onClick={() => navigate(-1)}>
                    <IoArrowBack className="icon" />
                  </button>
                  <h2 className="mb-md-0">
                    Conversations That Drive Better Health
                  </h2>
                </div>
              </h2>
            </Col>
            <Col md={6} className="text-end">
              <Button
                variant="outline-secondary"
                className="mobile-filter-toggle"
                onClick={() => navigate("/add-conversation")}
              >
                Start a New Conversation
              </Button>
            </Col>
          </Row>

          <Row className="g-4">
            {/* Main */}
            <Col lg={12}>
              <div className="results-stack community-stack community-stack-details">
                <Card className="result-card community-card community-details-card">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div>
                        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                          <div className="category-tag">
                            <Badge className="mb-md-0">{item.category}</Badge>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Card.Title className="result-title">
                      {item.title}
                    </Card.Title>

                    <div className="author-row">
                      <img
                        className="author-img"
                        src={item.author?.image || ex1}
                        alt="Author"
                      />
                      <span className="author-meta">
                        By{" "}
                        <Button
                          variant="link"
                          className="p-0 author-link"
                          onClick={() => navigate(`/profile/${item.id}`)}
                        >
                          {item.author?.name}
                        </Button>{" "}
                        · On {item.date}
                      </span>
                    </div>

                    <div className="details-body">
                      <p className="mb-0">{item.description}</p>
                    </div>

                    <div className="article-actions">
                      <Button variant="light" className="like-btn">
                        <AiOutlineLike /> 5 Likes
                      </Button>
                      <Button variant="light" className="follow-btn">
                        <IoMdShare />
                        Follow
                      </Button>
                    </div>
                    <div className="community-details-card-footer">
                      <div className="comments-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Order comments by</h5>
                        <Form.Select
                          size="sm"
                          value={orderBy}
                          onChange={(e) => setOrderBy(e.target.value)}
                          style={{ width: 190 }}
                        >
                          <option>Most Recent</option>
                          <option>Most Liked</option>
                        </Form.Select>
                      </div>

                      <div className="comments-list mt-3">
                        {sortedComments.map((c) => (
                          <Comment key={c.id} comment={c} />
                        ))}
                        {sortedComments.length === 0 && (
                          <div className="text-muted small">
                            No comments yet.
                          </div>
                        )}
                      </div>

                      {/* Add Comment */}
                      <div className="add-comment mt-3">
                        <div className="comment-top mb-2">
                          <div className="comment-top-left">
                            <div className="comment-avatar">
                              <img src={ex1} alt="" />
                            </div>
                            <div className="comment-user">By John Robinson</div>
                            <div className="comment-time">On 26th Aug, 25</div>
                          </div>
                          <div className="comment-top-right"></div>
                        </div>
                        <div className="comment-body">
                          <div className="comment-content">
                            <Form onSubmit={addComment} className="flex-1">
                              <div className="composer-ck">
                                <CKEditor
                                  editor={ClassicEditor}
                                  data={newComment} // HTML string
                                  onReady={(editor) => {
                                    // Optional: set a min height like your old textarea
                                    editor.ui.view.editable.element.style.minHeight =
                                      "150px";
                                  }}
                                  onChange={(event, editor) => {
                                    const data = editor.getData(); // HTML
                                    setNewComment(data);
                                  }}
                                  config={{
                                    placeholder: "Write a comment...",
                                    toolbar: [
                                      "bold",
                                      "italic",
                                      "underline",
                                      "|",
                                      "bulletedList",
                                      "numberedList",
                                      "|",
                                      "link",
                                      "blockQuote",
                                      "|",
                                      "insertImage", // ✅ image
                                      "mediaEmbed", // ✅ video
                                      "|",
                                      "undo",
                                      "redo",
                                    ],
                                  }}
                                />
                              </div>
                              <div className="text-end mt-3">
                                <Button type="submit" size="xl">
                                  Post
                                </Button>
                              </div>
                            </Form>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </Col>
          </Row>
        </div>
      </Container>
    </>
  );
}

function Comment({ comment }) {
  return (
    <div className="comment">
      <div className="comment-top mb-2">
        <div className="comment-top-left">
          <div className="comment-avatar">
            <img src={ex1} alt={comment.user} />
          </div>
          <div className="comment-user">{comment.user}</div>
          <div className="comment-time">{comment.time}</div>
        </div>
        <div className="comment-top-right"></div>
      </div>

      <div className="comment-body">
        <div className="comment-content">{comment.content}</div>

        <div className="comment-actions">
          <Button variant="light" className="like-btn">
            <AiOutlineLike /> {comment.likes} Likes
          </Button>
          <Button variant="light" className="follow-btn">
            Reply <BsReply />
          </Button>
        </div>

        {Array.isArray(comment.replies) && comment.replies.length > 0 && (
          <div className="replies">
            {comment.replies.map((r) => (
              <div key={r.id} className="reply">
                <div className="comment-top">
                  <div className="comment-top-left">
                    <div className="comment-avatar">
                      <img src={ex1} alt={r.user} />
                    </div>
                    <div className="comment-user">{r.user}</div>
                    <div className="comment-time">{r.time}</div>
                  </div>
                  <div className="comment-top-right">
                    <DropdownButton
                      align="end"
                      title={<BsThreeDotsVertical />}
                      id="dropdown-menu-align-end"
                    >
                      <Dropdown.Item eventKey="1">
                        <FiEdit2 /> Edit
                      </Dropdown.Item>
                      <Dropdown.Item eventKey="2">
                        <AiOutlineDelete /> Delete
                      </Dropdown.Item>
                    </DropdownButton>
                  </div>
                </div>
                <div className="comment-body">
                  <div className="comment-content">{r.content}</div>
                  <div className="comment-actions">
                    <Button variant="light" className="like-btn">
                      <AiOutlineLike /> {r.likes} Likes
                    </Button>
                    <Button variant="light" className="follow-btn">
                      Reply <BsReply />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
